import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Popup } from 'react-leaflet';
import L from 'leaflet';
import ZoomIndependentMarker from '../ZoomIndependentMarker';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../../firebase-config';

const useFirestoreEmulator = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true' || process.env.REACT_APP_ENVIRONMENT === 'emulator';

const swimZoneIcon = new L.Icon({
    iconUrl: '/swimzone.png',
    iconSize: [44, 72], // Slightly taller and narrower
    iconAnchor: [22, 72], // Anchor at bottom center
    popupAnchor: [0, -72]
});

const SwimZoneLayer = ({ visible = true }) => {
    const [swimZones, setSwimZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewImages, setPreviewImages] = useState([]);
    const [previewIndex, setPreviewIndex] = useState(0);

    const formatTimestamp = (value) => {
        if (!value) return 'N/A';
        if (typeof value === 'string') return value;
        if (value instanceof Date) return value.toLocaleString();
        if (value.seconds !== undefined) {
            return new Date(value.seconds * 1000).toLocaleString();
        }
        return String(value);
    };

    const getIstDateStr = (value) => {
        if (!value) return null;
        const date = value instanceof Date
            ? value
            : value.seconds !== undefined
                ? new Date(value.seconds * 1000)
                : new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        const istString = date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
        const istDate = new Date(istString);
        const day = String(istDate.getDate()).padStart(2, '0');
        const month = String(istDate.getMonth() + 1).padStart(2, '0');
        const year = istDate.getFullYear();
        return `${year}-${month}-${day}`;
    };

    const normalizePhotoUrls = (value) => {
        if (!value) return [];
        const rawList = Array.isArray(value) ? value : [value];
        return rawList
            .flatMap((item) => String(item).split(','))
            .map((item) => item.trim())
            .filter(Boolean);
    };

    const toPreviewItem = (url) => {
        if (!url) return null;
        const match = String(url).match(/(?:id=|\/d\/)([a-zA-Z0-9_-]+)/);
        if (match && match[1]) {
            const fileId = match[1];
            return {
                type: 'drive',
                url: `https://drive.google.com/file/d/${fileId}/preview`
            };
        }
        return { type: 'image', url: String(url) };
    };

    const handleOpenPreview = useCallback((urls) => {
        if (!urls || urls.length === 0) return;
        setPreviewImages(urls);
        setPreviewIndex(0);
        setIsPreviewOpen(true);
    }, []);

    const handleClosePreview = useCallback(() => {
        setIsPreviewOpen(false);
        setPreviewImages([]);
        setPreviewIndex(0);
    }, []);

    useEffect(() => {
        if (!visible) return;

        setLoading(true);
        
        // Robust IST Date Construction (dd-mm-yyyy)
        // 1. Get current time in IST
        const now = new Date();
        const istString = now.toLocaleString("en-US", {timeZone: "Asia/Kolkata"});
        const istDate = new Date(istString);
        
        // 2. Format components manually to match '03-02-2026'
        const day = String(istDate.getDate()).padStart(2, '0');
        const monthNumber = String(istDate.getMonth() + 1).padStart(2, '0');
        const year = istDate.getFullYear();
        
        const todayDocId = `${day}-${monthNumber}-${year}`;
        const todayDateStr = `${year}-${monthNumber}-${day}`;
        
        console.log(`🏊 [SwimZoneLayer] Target Date (IST): ${todayDocId}`);
        console.log(`🏊 [SwimZoneLayer] Subscribing to: swimzone/${todayDocId}/entries`);

        // Subscribe to real-time updates for today's entries
        const entriesRef = collection(db, 'swimzone', todayDocId, 'entries');

        const processEntriesSnapshot = (snapshot) => {
            if (snapshot.empty) {
                setSwimZones((prev) => (prev.length === 0 ? prev : []));
                setLoading(false);
                return;
            }
            const zones = [];

            snapshot.forEach((entryDoc) => {
                const entry = entryDoc.data();
                console.log(`🏊 [SwimZoneLayer] Processing entry ${entryDoc.id}:`, entry);

                const entryDateStr = entry.date_str || getIstDateStr(entry.report_datetime || entry.created_at);
                if (entryDateStr && entryDateStr !== todayDateStr) {
                    return;
                }

                // Check for valid coordinates
                let lat = entry.lat || entry.latitude;
                let lng = entry.lng || entry.longitude;

                // Handle Firestore GeoPoint fields (co_ordinates or coordinates)
                if (!lat || !lng) {
                    const geoField = entry.co_ordinates || entry.coordinates;
                    if (geoField) {
                        if (geoField.latitude !== undefined) {
                            lat = geoField.latitude;
                            lng = geoField.longitude;
                        } else if (Array.isArray(geoField) && geoField.length >= 2) {
                            lat = geoField[0];
                            lng = geoField[1];
                        } else if (geoField.lat !== undefined) {
                            lat = geoField.lat;
                            lng = geoField.lng;
                        } else if (typeof geoField === 'string' && geoField.includes(',')) {
                            const parts = geoField.split(',');
                            lat = parseFloat(parts[0]);
                            lng = parseFloat(parts[1]);
                        }
                    }
                }

                // Parse if strings
                if (typeof lat === 'string') lat = parseFloat(lat);
                if (typeof lng === 'string') lng = parseFloat(lng);

                console.log(`🏊 [SwimZoneLayer] Extracted coords for ${entryDoc.id}:`, lat, lng);

                if (
                    lat !== undefined && lat !== null &&
                    lng !== undefined && lng !== null &&
                    !isNaN(lat) && !isNaN(lng) &&
                    lat !== 0 && lng !== 0
                ) {
                    zones.push({
                        id: entryDoc.id,
                        date: todayDocId,
                        lat: lat,
                        lng: lng,
                        ...entry
                    });
                } else {
                    console.warn(`🏊 [SwimZoneLayer] Invalid coordinates for ${entryDoc.id}`);
                }
            });

            // Only update state if data actually changed to prevent unnecessary re-renders
            setSwimZones((prev) => {
                const prevIds = new Set(prev.map(z => z.id));
                const newIds = new Set(zones.map(z => z.id));
                if (prevIds.size === newIds.size && [...prevIds].every(id => newIds.has(id))) {
                    const hasChanges = zones.some(newZone => {
                        const oldZone = prev.find(z => z.id === newZone.id);
                        if (!oldZone) return true;
                        return oldZone.lat !== newZone.lat || oldZone.lng !== newZone.lng ||
                            oldZone.beach_name !== newZone.beach_name || oldZone.point_name !== newZone.point_name ||
                            oldZone.date_str !== newZone.date_str;
                    });
                    if (!hasChanges) return prev;
                }
                return zones;
            });
            setLoading(false);
        };

        if (useFirestoreEmulator) {
            const poll = async () => {
                try {
                    const snapshot = await getDocs(entriesRef);
                    processEntriesSnapshot(snapshot);
                } catch (err) {
                    console.error("🏊 [SwimZoneLayer] Swim zones poll error:", err);
                    setLoading(false);
                }
            };
            poll();
            const intervalId = setInterval(poll, 5000);
            return () => clearInterval(intervalId);
        }

        const unsubscribe = onSnapshot(entriesRef, processEntriesSnapshot, (error) => {
            console.error("🏊 [SwimZoneLayer] Error fetching swim zones:", error);
            setLoading(false);
        });

        // Cleanup subscription on unmount or when visible changes
        return () => unsubscribe();
    }, [visible]);

    // Memoize zones to prevent unnecessary re-renders
    const memoizedZones = useMemo(() => swimZones, [swimZones]);

    if (!visible) return null;

    return (
        <>
            {memoizedZones.map((zone) => {
                const imageUrls = normalizePhotoUrls(zone.photo_urls)
                    .map(toPreviewItem)
                    .filter(Boolean);

                return (
                    <ZoomIndependentMarker
                        key={zone.id}
                        position={[zone.lat, zone.lng]}
                        icon={swimZoneIcon}
                    >
                        <Popup className="custom-popup swimzone-popup">
                            <h4>Swim Zone</h4>
                            <hr />
                            <div className="popup-content">
                                <div className="info-row">
                                    <span className="info-label">Date</span>
                                    <span className="info-value">{zone.date_str || formatTimestamp(zone.date)}</span>
                                </div>
                                {zone.beach_name && (
                                    <div className="info-row">
                                        <span className="info-label">Beach Name</span>
                                        <span className="info-value">{zone.beach_name}</span>
                                    </div>
                                )}
                                {zone.point_name && (
                                    <div className="info-row">
                                        <span className="info-label">Point Name</span>
                                        <span className="info-value">{zone.point_name}</span>
                                    </div>
                                )}
                                {zone.report_datetime && (
                                    <div className="info-row">
                                        <span className="info-label">Report Time</span>
                                        <span className="info-value">{formatTimestamp(zone.report_datetime)}</span>
                                    </div>
                                )}
                                {zone.name && (
                                    <div className="info-row">
                                        <span className="info-label">Name</span>
                                        <span className="info-value">{zone.name}</span>
                                    </div>
                                )}
                                {zone.description && (
                                    <div className="info-row">
                                        <span className="info-label">Description</span>
                                        <span className="info-value">{zone.description}</span>
                                    </div>
                                )}
                                <div className="info-row">
                                    <span className="info-label">Coordinates</span>
                                    <span className="info-value">{zone.lat.toFixed(6)}, {zone.lng.toFixed(6)}</span>
                                </div>
                                {imageUrls.length > 0 && (
                                    <div className="info-row swimzone-preview-row">
                                        <span className="info-label">Photos</span>
                                        <button
                                            type="button"
                                            className="swimzone-preview-button"
                                            onClick={() => handleOpenPreview(imageUrls)}
                                        >
                                            Image Preview
                                        </button>
                                    </div>
                                )}
                            </div>
                        </Popup>
                    </ZoomIndependentMarker>
                );
            })}
            {isPreviewOpen && typeof document !== 'undefined' && createPortal(
                <div className="swimzone-preview-overlay" onClick={handleClosePreview}>
                    <div
                        className="swimzone-preview-modal"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="swimzone-preview-header">
                            <h5>Image Preview</h5>
                            <button
                                type="button"
                                className="swimzone-preview-close"
                                onClick={handleClosePreview}
                            >
                                x
                            </button>
                        </div>
                        <div className="swimzone-preview-body">
                            {previewImages.length > 0 && (
                                <>
                                    <div className="swimzone-preview-media">
                                        {previewImages[previewIndex]?.type === 'drive' ? (
                                            <iframe
                                                src={previewImages[previewIndex].url}
                                                title={`Swim zone ${previewIndex + 1}`}
                                                className="swimzone-preview-frame"
                                                allow="autoplay"
                                            />
                                        ) : (
                                            <img
                                                src={previewImages[previewIndex]?.url}
                                                alt={`Swim zone ${previewIndex + 1}`}
                                                className="swimzone-preview-image"
                                            />
                                        )}
                                    </div>
                                    {previewImages.length > 1 && (
                                        <div className="swimzone-preview-controls">
                                            <button
                                                type="button"
                                                className="swimzone-preview-nav"
                                                onClick={() =>
                                                    setPreviewIndex((index) =>
                                                        (index - 1 + previewImages.length) % previewImages.length
                                                    )
                                                }
                                            >
                                                Prev
                                            </button>
                                            <span className="swimzone-preview-count">
                                                {previewIndex + 1} / {previewImages.length}
                                            </span>
                                            <button
                                                type="button"
                                                className="swimzone-preview-nav"
                                                onClick={() =>
                                                    setPreviewIndex((index) => (index + 1) % previewImages.length)
                                                }
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default SwimZoneLayer;
